import { getScale, readableColor, toHex } from "color2k";
import { HotHeatMapBaseMeta, HotHeatMapResult } from "..";


// Processes heat map data using based on the manipulation of 'isg-hm-' classes stored in the hot header column name
// Workaround based on inability to store meta data with a handsontable instance and not wishing to use global variables

// 1. Transforms the cell value to a color based on the value and the max value in the column
// 2. Retrieves the background color from a custom class name stored in the column header class name
// 3. Caches the max value in the column header class name for future use

/**
 * Handles heat map calculations for double-valued columns in Handsontable grids.
 * Manages positive and negative value ranges, color scales, and storage of computed/cached values.
 */
export class HotHeatMapDoubleMeta extends HotHeatMapBaseMeta {

  // ---------------- Properties  --------------------

  /**
   * The maximum positive value in the column.
   */
  public maxPositive: number | null = null;

  /**
   * The minimum positive value in the column.
   */
  public minPositive: number | null = null;

  /**
   * The maximum negative value in the column.
   */
  public maxNegative: number | null = null;

  /**
   * The minimum negative value in the column.
   */
  public minNegative: number | null = null;

  /**
   * The background color for the maximum positive value.
   */
  public colorBgMaxPositive: string | null = null;

  /**
   * The background color for the minimum positive value.
   */
  public colorBgMinPositive: string | null = null;

  /**
   * The background color for the maximum negative value.
   */
  public colorBgMaxNegative: string | null = null;

  /**
   * The background color for the minimum negative value.
   */
  public colorBgMinNegative: string | null = null;

  // ---------------- Scale functions  ----------------

  /**
   * The color scale function for positive values.
   */
  private heatmapScalePositive: any | null = null;

  /**
   * The color scale function for negative values.
   */
  private heatmapScaleNegative: any | null = null;

  // ---------------- Constants  --------------------

  /**
   * Key for storing the maximum positive value.
   */
  protected readonly keyMaxPositive: string = "isg_hm_p_max_v_";

  /**
   * Key for storing the minimum positive value.
   */
  protected readonly keyMinPositive: string = "isg_hm_p_min_v_";

  /**
   * Key for storing the maximum negative value.
   */
  protected readonly keyMaxNegative: string = "isg_hm_n_max_v_";

  /**
   * Key for storing the minimum negative value.
   */
  protected readonly keyMinNegative: string = "isg_hm_n_min_v_";

  /**
   * Key for storing the background color for the maximum positive value.
   */
  protected readonly keyColorMaxPositive: string = "isg_hm_p_max_c_";

  /**
   * Key for storing the background color for the minimum positive value.
   */
  protected readonly keyColorMinPositive: string = "isg_hm_p_min_c_";

  /**
   * Key for storing the background color for the maximum negative value.
   */
  protected readonly keyColorMaxNegative: string = "isg_hm_n_max_c_";

  /**
   * Key for storing the background color for the minimum negative value.
   */
  protected readonly keyColorMinNegative: string = "isg_hm_n_min_c_";

  /**
   * Default background color for the maximum positive value.
   */
  public readonly colorDefaultBgMaxPositive: string = '#F8696B';

  /**
   * Default background color for the minimum positive value.
   */
  public readonly colorDefaultBgMinPositive: string = '#FFEB84';

  /**
   * Default background color for the maximum negative value.
   */
  public readonly colorDefaultBgMaxNegative: string = '#FFEB84';

  /**
   * Default background color for the minimum negative value.
   */
  public readonly colorDefaultBgMinNegative: string = '#63BE7B';

  // ---------------- Methods  --------------------

  /**
   * Initializes the heat map meta for a specific column and optional metadata.
   * Extracts cached values and color settings from the store.
   * @param column The column index.
   * @param meta Optional metadata string.
   */
  public init(column: number, meta?: string | null): void {

    this.store = meta ?? "";
    this.column = column;

    // Extract any precalcuated cached values from the store
    this.maxPositive = this.getMaxPositive();
    this.minPositive = this.getMinPositive();
    this.maxNegative = this.getMaxNegative();
    this.minNegative = this.getMinNegative();

    // Extract the colors from the store
    this.colorBgMaxPositive = this.getColorBgMaxPositive();
    this.colorBgMinPositive = this.getColorBgMinPositive();
    this.colorBgMaxNegative = this.getColorBgMaxNegative();
    this.colorBgMinNegative = this.getColorBgMinNegative();

    this.initColorRange();
  }

  /**
   * Initializes the positive and negative value ranges from the provided values.
   * @param values The array of values to analyze.
   */
  public initRangeValues(values: any[]): void {

    // Filter for positive values (including zero)
    const positiveValues = values.filter(value => value >= 0);
    this.setMaxPositive(positiveValues.length > 0 ? Math.max.apply(null, positiveValues) : null);
    this.setMinPositive(positiveValues.length > 0 ? Math.min.apply(null, positiveValues) : null);

    // Filter for negative values
    const negativeValues = values.filter(value => value < 0);
    this.setMaxNegative(negativeValues.length > 0 ? Math.max.apply(null, negativeValues) : null);
    this.setMinNegative(negativeValues.length > 0 ? Math.min.apply(null, negativeValues) : null);

  }

  // ---------------- HeatMap calculation  --------------------

  /**
   * Calculates the heat map colors for a given value.
   * Returns the result with the computed foreground and background colors.
   * @param value The value to calculate colors for.
   * @returns The result containing color information.
   */
  public calcHeatmapColors(value: number | null): HotHeatMapResult {

    let result: HotHeatMapResult = new HotHeatMapResult();

    if (value == null || value == undefined || value == 0) {

      this.colorHm = this.colorDefault;
      this.colorHmBg = this.colorDefaultBg;

      result.colorHm = this.colorHm;
      result.colorHmBg = this.colorHmBg;
      return result;
    }

    if (value > 0 && this.heatmapScalePositive != null) {
      const normalizedPosValue: number = this.calculatePercentagePosition(value, this.minPositive!, this.maxPositive!) ?? 0;
      if (isNaN(normalizedPosValue)) {
        this.colorHmBg = this.colorDefaultBg;
        this.colorHm = this.colorDefault;
      }
      else {
        this.colorHmBg = toHex(this.heatmapScalePositive!(normalizedPosValue));
        this.colorHm = readableColor(this.colorHmBg!);
      }
    }
    else if (value < 1 && this.heatmapScaleNegative != null) {
      const normalizedNegValue: number = this.calculatePercentagePosition(value, this.minNegative!, this.maxNegative!) ?? 0;

      if (isNaN(normalizedNegValue)) {
        this.colorHmBg = this.colorDefaultBg;
        this.colorHm = this.colorDefault;
      }
      else {
        this.colorHmBg = toHex(this.heatmapScaleNegative!(normalizedNegValue));
        this.colorHm = readableColor(this.colorHmBg!);
      }
    }

    result.colorHm = this.colorHm;
    result.colorHmBg = this.colorHmBg;
    return result;
  }

  /**
   * Initializes the color range scales for positive and negative values.
   */
  public initColorRange(): void {

    this.heatmapScalePositive = getScale(this.colorBgMinPositive!, this.colorBgMaxPositive!);
    this.heatmapScaleNegative = getScale(this.colorBgMinNegative!, this.colorBgMaxNegative!);

  }

  /**
   * Removes all cached calculated values from the store, leaving only color values.
   * Resets the min/max properties to null.
   */
  public clearStore(): void {

    if (this.store == null || this.store == undefined) {
      this.store = "";
    }

    const keyMatches: string[] = [
      "\\" + this.keyMaxPositive + this.expr,
      "\\" + this.keyMinPositive + this.expr,
      "\\" + this.keyMaxNegative + this.expr,
      "\\" + this.keyMinNegative + this.expr
    ];

    for (let keyMatch of keyMatches) {

      const regex = new RegExp(keyMatch, "g");

      let matches: RegExpMatchArray | null = this.store.match(regex);

      if (matches != null && matches.length > 0) {
        this.store = this.store.replace(matches[0], "");
      }
    }

    this.store = this.store.trim();

    this.maxPositive = null;
    this.minPositive = null;
    this.maxNegative = null;
    this.minNegative = null;

  }

  // ---------------- Value Accessors  --------------------

  /**
   * Gets the maximum positive value from the store.
   * @returns The maximum positive value or null.
   */
  public getMaxPositive(): number | null {

    return this.getStoreValue(this.keyMaxPositive, true);
  }

  /**
   * Gets the minimum positive value from the store.
   * @returns The minimum positive value or null.
   */
  public getMinPositive(): number | null {

    return this.getStoreValue(this.keyMinPositive, true);
  }

  /**
   * Gets the maximum negative value from the store.
   * @returns The maximum negative value or null.
   */
  public getMaxNegative(): number | null {

    return this.getStoreValue(this.keyMaxNegative, true);
  }

  /**
   * Gets the minimum negative value from the store.
   * @returns The minimum negative value or null.
   */
  public getMinNegative(): number | null {

    return this.getStoreValue(this.keyMinNegative, true);
  }

  /**
   * Sets the maximum positive value in the store and updates the property.
   * @param value The value to set.
   */
  public setMaxPositive(value: number | null): void {

    this.setStoreValue(value, this.keyMaxPositive);
    this.maxPositive = value ?? 1;
  }

  /**
   * Sets the minimum positive value in the store and updates the property.
   * @param value The value to set.
   */
  public setMinPositive(value: number | null): void {

    this.setStoreValue(value, this.keyMinPositive);
    this.minPositive = value ?? 0;
  }

  /**
   * Sets the maximum negative value in the store and updates the property.
   * @param value The value to set.
   */
  public setMaxNegative(value: number | null): void {

    this.setStoreValue(value, this.keyMaxNegative);
    this.maxNegative = value ?? 0;
  }

  /**
   * Sets the minimum negative value in the store and updates the property.
   * @param value The value to set.
   */
  public setMinNegative(value: number | null): void {

    this.setStoreValue(value, this.keyMinNegative);
    this.minNegative = value ?? 0;
  }

  /**
   * Gets the background color for the maximum positive value from the store or returns the default.
   * @returns The background color as a string.
   */
  public getColorBgMaxPositive(): string | null {

    return this.getStoreValue(this.keyColorMaxPositive, false) ?? this.colorDefaultBgMaxPositive;
  }

  /**
   * Gets the background color for the minimum positive value from the store or returns the default.
   * @returns The background color as a string.
   */
  public getColorBgMinPositive(): string | null {

    return this.getStoreValue(this.keyColorMinPositive, false) ?? this.colorDefaultBgMinPositive;
  }

  /**
   * Gets the background color for the maximum negative value from the store or returns the default.
   * @returns The background color as a string.
   */
  public getColorBgMaxNegative(): string | null {

    return this.getStoreValue(this.keyColorMaxNegative, false) ?? this.colorDefaultBgMaxNegative;
  }

  /**
   * Gets the background color for the minimum negative value from the store or returns the default.
   * @returns The background color as a string.
   */
  public getColorBgMinNegative(): string | null {

    return this.getStoreValue(this.keyColorMinNegative, false) ?? this.colorDefaultBgMinNegative;
  }

  // ---------------- Set color for the heatmap ranges --------------------
  // This is expected to read from the column header class name/configuration, here for completeness

  /**
   * Sets the background color for the maximum positive value.
   * @param value The color value to set.
   */
  public setColorBgMaxPositive(value: any | null): void {

    this.setStoreValue(value, this.keyColorMaxPositive);
    this.colorBgMaxPositive = value;
  }

  /**
   * Sets the background color for the minimum positive value.
   * @param value The color value to set.
   */
  public setColorBgMinPositive(value: any | null): void {

    this.setStoreValue(value, this.keyColorMinPositive);
    this.colorBgMinPositive = value;
  }

  /**
   * Sets the background color for the maximum negative value.
   * @param value The color value to set.
   */
  public setColorBgMaxNegative(value: any | null): void {

    this.setStoreValue(value, this.keyColorMaxNegative);
    this.colorBgMaxNegative = value;
  }

  /**
   * Sets the background color for the minimum negative value.
   * @param value The color value to set.
   */
  public setColorBgMinNegative(value: any | null): void {

    this.setStoreValue(value, this.keyColorMaxNegative);
    this.colorBgMinNegative = value;
  }

}

